import { autoinject } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { AssistanceService } from 'app/shared/services/assistance-service';
import { State } from 'app/shared/lib/state';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { VerifyBy, IAssistanceRequest, IdentificationMapping } from 'app/shared/models/assistance-models';
import { LoginContext } from 'app/shared/lib/login-context';
import { IApiAuthenticationResults } from 'app/shared/lib/api/api-types';
import { guid } from 'app/shared/lib/guid';


export type RecoverType = 'password' | 'username';

@autoinject()
export class RecoverViewModel {

    public subject: RecoverType = 'password';
    public readonly method: VerifyBy = 'email';
    public contactInfo: string = '';
    public errorMessage: string = '';

    protected readonly state: State = new State();

    protected id: guid = guid();

    constructor(
        private readonly env: EnvironmentService,
        private readonly router: Router,
        private readonly loginContext: LoginContext,
        private readonly service: AssistanceService) { }

    protected activate({ subject, method }: { subject: RecoverType, method: VerifyBy }): void {
        if(subject !== undefined) {
            this.subject = subject;
        }
        // INFO [Matt] Disabled until methods other than email are supported.
        //if (method !== undefined) {
        //    this.method = method;
        //}
    }

    protected async process() {
        try {
            this.state.beginSave();

            let request: Promise<IApiAuthenticationResults>;
            if(this.subject === 'password') {
                request = this.service.resetPassword({
                    ...this.generateRequest('Change Password', 'change-password'),
                    credentials: {
                        loginName: this.loginContext.loginName
                    }
                });
            }
            else if(this.subject === 'username') {
                if(!IdentificationMapping.has(this.method)) {
                    return console.error(`Verification method '${this.method}' is not mapped to an identification property.`);
                }
                const method = IdentificationMapping.get(this.method)!;
                request = this.service.forgotLoginName({
                    ...this.generateRequest('Retrieve Login Name', 'checkin'),
                    identification: {
                        [method]: this.contactInfo
                    }
                });
            }

            const { isSuccessful } = await request!;
            if(isSuccessful) {
                this.errorMessage = '';
                this.router.navigateToRoute(this.subject === 'password' ? 'change-password' : 'verify');
            }
            else {
                this.errorMessage = 'Unable to send verification code.';
            }
        }
        catch(e) {
            console.log(e);
            this.errorMessage = 'Unable to send verification code.';
        }
        finally {
            this.state.endSave();
        }
    }

    protected generateRequest(title: string, route: string): IAssistanceRequest {
        return {
            application: { code: this.env.config.application },
            verifyBy: this.method,
            targetLink: {
                title: `${title} - ${this.env.config.description}`,
                baseUrl: this.env.baseUrl,
                href: decodeURIComponent(this.router.generate(route, { code: '${code}' }))
            }
        };
    }

    ////****************** Functions ************************************************////
    protected validate(email: string): boolean {
        return /^\s*\S+@\S+\.\S+\s*$/.test(email);
    }

    protected getPlaceholder(method: string): string {
        switch(method) {
            // INFO [JP] if other methods of verification are added, additional cases can be handled here
            case 'email':
            default:
                return 'cd:member.emailAddress.$';
        }
    }
}
